import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import useTimekeepingCtx, {
  TimekeepingContext,
} from "../../context/domain/timekeeping/TimekeepingContext";
import { useUiText } from "../../hooks/useUiText";
import { UI_COPY } from "../../config";
import { addStringTimes } from "../../utils/datetime";

import WeeklyShiftSummary from "../../features/timekeeping/workerClock/WeeklyShiftSummary";
import WeeklyTimesheetReminder from "../../features/timekeeping/workerClock/components/WeeklyTimesheetReminder";
import ShiftDateDisplay from "../../features/timekeeping/workerClock/infoBoxes/ShiftDateDisplay";
import EditShift from "../../features/timekeeping/workerClock/EditShift";

import { Button } from "../../components/ui";

/**
 * WorkerSchedule Page Component
 *
 * Serves as the interface for viewing the worker weekly shift schedule.
 *
 * Features:
 * - Provides context for all child timekeeping components
 * - Displays weekly shift summary for the user
 * - Allows navigation back to the daily clock-in view
 * - Provides access to shift editing functionality
 * - Supports optional URL params: /worker-schedule/:date?/user/:userId?
 *
 * @returns {JSX.Element} The WorkerSchedule page UI
 */
const WorkerSchedule = () => {
  const { date: urlDate, userId: urlUserId } = useParams();
  
  // Parse userId from URL if provided
  const parsedUserId = urlUserId ? parseInt(urlUserId, 10) : null;
  
  // Initialize context with URL params if provided
  const timekeepingCtx = useTimekeepingCtx(urlDate || null, parsedUserId);
  const {
    date,
    setDate,
    dailyShiftData,
    clearMessage,
    setDailyShiftData,
    fetchWeeklyShifts,
  } = timekeepingCtx;
  const { language, t } = useUiText();
  
  // Update date if URL param changes
  useEffect(() => {
    if (urlDate && urlDate !== date) {
      setDate(urlDate);
    }
  }, [urlDate, date, setDate]);

  useEffect(() => {
    document.title = t(UI_COPY.schedule.pageTitle);
  }, [language, t]);

  const [editShift, setEditShift] = useState(false);

  // To allow adding a new shift on this day when editing the day
  /**
   * Appends a blank complete-shift row starting at the previous finish time.
   *
   * @returns {void}
   */
  const addBlankShift = () => {
    const prevTimeOut =
      dailyShiftData[dailyShiftData.length - 1]?.finishTime || ""; // Add 4 hours to prevTimeOut (format: "HH:mm"), default to "17:00" if empty
    const newTimeOut = prevTimeOut
      ? addStringTimes(prevTimeOut, "4:00")
      : "17:00";

    setDailyShiftData((prev) => [
      ...prev,
      {
        id: null, // Leave blank ID so we know to save it to API backend
        stage: "Complete Shift",
        date: date,
        job: "",
        workScope: "",
        workDescription: "",
        startTime: prevTimeOut,
        finishTime: newTimeOut,
      },
    ]);
  };

  return (
    <TimekeepingContext.Provider value={timekeepingCtx}>
      <div className="p-0 max-w-[1024px] mx-auto">
        <WeeklyTimesheetReminder className="m-2 mt-4" />
        {!editShift ? (
          <div className="p-2">
            <WeeklyShiftSummary setEditShift={setEditShift} />
          </div>
        ) : (
          <div className="bg-white rounded-md shadow-md p-6 mb-4">
            {/* Header */}
            <div>
              <ShiftDateDisplay />
            </div>
            {dailyShiftData.map((shift, index) => {
              const prevTimeOut = dailyShiftData[index - 1]?.finishTime || "";
              const nextTimeIn = dailyShiftData[index + 1]?.startTime || "";
              return (
                <div key={shift.id ?? `new-shift-${index}`} className="mb-4">
                  <EditShift
                    dataValidationFileds={[
                      {
                        prevTimeOut,
                        nextTimeIn,
                      },
                    ]}
                    shiftDataIn={shift}
                    setEditShift={setEditShift}
                    index={index}
                  />
                </div>
              );
            })}
            {/* Footer Buttons */}
            <Button
              onClick={() => {
                addBlankShift();
              }}
              variant={"custom"}
              text={t(UI_COPY.clockIn.addAnotherShift)}
              icon={"plus"}
              className="py-1 colspan-2 px-6 mt-4 sm:mt-4 w-full bg-gray-200 hover:bg-gray-300 text-gray-900"
            />
            <div className="mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  fetchWeeklyShifts();
                  clearMessage();
                  setEditShift(false);
                }}
                text={t(UI_COPY.schedule.returnToWeeklySchedule)}
                className="py-2 px-6 w-full"
              />
            </div>
          </div>
        )}
      </div>
    </TimekeepingContext.Provider>
  );
};

export default WorkerSchedule;
